import type { WeeklyCalculatorFormValues } from "./types/form"

// ============================================================================
// WEEKLY CALCULATOR TYPES
// ============================================================================

/**
 * Data for a single time interval
 */
export interface IntervalData {
  dayIndex: number
  dayKey: string
  intervalIndex: number
  time: string
  calls: number
  agents: number
  scheduledAgents: number
}

/**
 * Weekly calculation result from API
 */
export interface WeeklyCalculationResult {
  result: {
    agentsFte: number
    agentsPer30MinsMax: number
    callsPer30MinsMax: number
    callsPerWeek: number
  }
  intervals: IntervalData[]
  calculationWarnings: string[]
  savedInputId: string
  savedResultId: string
}

/**
 * Call the weekly prediction edge function to calculate staffing
 */
export async function callWeeklyPredictionAPI(
  formValues: WeeklyCalculatorFormValues,
): Promise<WeeklyCalculationResult | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error("Supabase credentials not configured")
      return null
    }

    // Convert string form values to numbers for API
    // Note: No period field - backend determines calculation type from URL param
    const apiPayload = {
      incomingContacts: parseInt(formValues.incomingContacts, 10),
      averageHandleTimeSeconds: parseFloat(formValues.averageHandleTimeSeconds),
      requiredServiceLevel: parseFloat(formValues.requiredServiceLevel),
      targetAnswerTimeSeconds: parseFloat(formValues.targetAnswerTimeSeconds),
      shrinkage: parseFloat(formValues.shrinkage),
      maxOccupancy: parseFloat(formValues.maxOccupancy),
      averagePatienceSeconds: parseFloat(formValues.averagePatienceSeconds),
      workingHoursPerWeek: parseFloat(formValues.workingHoursPerWeek),
      reportingInterval: formValues.reportingInterval,
      hoursOpenPerDay: parseFloat(formValues.hoursOpenPerDay),
      weekStart: formValues.weekStart,
      distributionMonday: parseFloat(formValues.distributionMonday),
      distributionTuesday: parseFloat(formValues.distributionTuesday),
      distributionWednesday: parseFloat(formValues.distributionWednesday),
      distributionThursday: parseFloat(formValues.distributionThursday),
      distributionFriday: parseFloat(formValues.distributionFriday),
      distributionSaturday: parseFloat(formValues.distributionSaturday),
      distributionSunday: parseFloat(formValues.distributionSunday),
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/prediction?type=weekly`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${supabaseAnonKey}`,
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify(apiPayload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
      console.error("Weekly prediction API error:", errorData)
      return null
    }

    const result = await response.json()
    return result as WeeklyCalculationResult
  } catch (error) {
    console.error("Error calling weekly prediction API:", error)
    return null
  }
}
